import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Boxes, DollarSign, Archive, ShieldAlert, AlertTriangle, TrendingDown, HelpCircle, X, PackageX,
  Download, Search,
} from 'lucide-react';
import { useInventorySource } from '../components/Inventory/InventorySource';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import InventoryTrend from '../components/Inventory/InventoryTrend';
import AgeBandChart from '../components/Inventory/AgeBandChart';
import AgeBandByCostCenter from '../components/Inventory/AgeBandByCostCenter';
import ValueCategoryTable from '../components/Inventory/ValueCategoryTable';
import CategoryCompositionBar from '../components/Inventory/CategoryCompositionBar';
import PartCategoryTable from '../components/Inventory/PartCategoryTable';
import PartCategoryChart from '../components/Inventory/PartCategoryChart';
import DataDisclosure from '../components/Inventory/DataDisclosure';
import CrossYardPanel from '../components/Inventory/CrossYardPanel';
import WorklistTable from '../components/Inventory/WorklistTable';
import StatTile from '../components/Inventory/StatTile';
import PendingPanel from '../components/Inventory/PendingPanel';
import LevelBar from '../components/Inventory/LevelBar';
import { fmtMoney, fmtNumber, fmtDate, fmtBin } from '../components/Inventory/format';
import { c, t, sp, r, btn, card, tbl } from '../theme';

const EXCEPTION_LABELS = {
  aged:               'idle 12+ months',
  never_issued:       'never used',
  over_max:           'over a set maximum',
  over_max_no_max:    'flagged over-max, no maximum set',
  under_min:          'below minimum',
  under_min_critical: 'critical spares below minimum',
  idle_24_months:     'idle 24+ months',
};

const BAND_LABELS = {
  '0_6': '0–6 months', '6_12': '6–12 months', '12_24': '12–24 months',
  '24_plus': '24+ months', never: 'never used',
};

// Youngest to oldest, never-issued last. Object key order is not a contract, so
// the dropdown reads from an explicit list — sorted alphabetically it would run
// 0–6, 12–24, 24+, 6–12, never, which is an age ramp that goes backwards.
const AGE_BAND_ORDER = ['0_6', '6_12', '12_24', '24_plus', 'never'];

// Division and cost center are the same hierarchy here — there is no level
// above cost center in the portal, and the business treats them as one. The
// board says "cost center" throughout, which is the word the rest of the
// portal uses: the access model grants on cost centers, and Overtime, Fuel
// Report and Staffing all name it that way in their own screens and errors.
export default function Inventory() {
  const [level, setLevel]         = useState('L1');
  const [ccFilter, setCcFilter]   = useState('all');
  const [exception, setException] = useState(null);
  const [band, setBand]           = useState(null);
  const [classification, setClassification] = useState(null);
  const [sortBy, setSortBy]       = useState({ key: 'total_value', dir: 'desc' });
  const [partCategory, setPartCategory] = useState(null);

  // Two pieces of state for one filter: what the reader is typing, and what has
  // been asked of the server. Every other filter here is a select, where one
  // click is one deliberate change and can query immediately. Typing is not —
  // bound directly, "L000974" would fire seven requests and render the results
  // of whichever returned last, which is not necessarily the one for what is on
  // screen.
  const [partSearch, setPartSearch] = useState('');
  const [partQuery, setPartQuery]   = useState('');

  useEffect(() => {
    const id = setTimeout(() => setPartQuery(partSearch.trim()), 300);
    return () => clearTimeout(id);
  }, [partSearch]);

  const scoped = ccFilter !== 'all';

  const { inventoryViewIsUniversal } = useAuth();
  const { api, queryKey, title, subtitle, headerLink } = useInventorySource();

  // The cost center is part of the key, not just the request: keyed only by
  // name, React Query would serve the cached company-wide answer and the tiles
  // would sit at the total while the table below showed one yard.
  const { data: summary, isLoading: summaryLoading, error } = useQuery({
    queryKey: queryKey('inventory-summary', ccFilter),
    queryFn:  () => api.get('/inventory/summary', { params: scoped ? { cc_id: ccFilter } : {} }),
  });

  // Scoped responses carry only the selected cost center, so the picker is fed
  // from the unscoped summary — otherwise choosing a cost center empties the
  // list you would use to choose a different one.
  const { data: allCostCenters } = useQuery({
    queryKey: queryKey('inventory-summary', 'all'),
    queryFn:  () => api.get('/inventory/summary'),
  });

  const ccOptions  = allCostCenters?.by_cost_center || [];
  // Tables read the scoped response, so they narrow with everything else.
  const tableRows  = summary?.by_cost_center || [];
  const selectedCc = ccOptions.find((cc) => cc.cc_id === ccFilter);

  const { data: ccDetail, isLoading: ccDetailLoading } = useQuery({
    queryKey: queryKey('inventory-costcenter', ccFilter, exception, band, classification),
    queryFn:  () => api.get(`/inventory/costcenter/${ccFilter}`, {
      params: {
        ...(exception ? { exception } : {}),
        ...(band ? { band } : {}),
        ...(classification ? { classification } : {}),
      },
    }),
    enabled:  ccFilter !== 'all',
  });

  // Sets an exception filter and makes sure a list is on screen to show it.
  //
  // Callers are now the L2 tiles, where this is a toggle over the list already
  // in front of the reader, and the two inline "list them" links, which say in
  // words that they navigate. The L0/L1 tiles no longer call it: those levels
  // report, and a reader who wants a list goes to L2 and filters it themselves.
  //
  // It must never invent a cost center. This previously did: with nothing
  // selected it set ccFilter to ccOptions[0], so any tile on L0 silently
  // replaced the $3.9M company page with the first yard in the list (Crown MRF,
  // $591k) while the heading still named the whole company and the level still
  // said L0.
  // The tile appeared to filter to something the reader never chose.
  //
  // The scope stays the reader's: this only ever sets the population filter and
  // moves to the level that can show it. L2 is that level — it lists parts at
  // any scope, one yard or all of them.
  function drillTo(key) {
    if (exception === key) { setException(null); return; }
    setException(key);
    setBand(null);
    setLevel('L2');
  }

  // Two callers, two behaviors, and the difference is deliberate.
  //
  // A dropdown is a filter control: it narrows what you are looking at and
  // leaves you where you are. Clicking a MARK — a tile, a bar, a table row —
  // is a request to see that population, so it moves to the level that lists
  // it. Making the dropdown navigate (which it briefly did) meant choosing an
  // age band silently threw the reader off L0 onto a part list, and the band
  // highlight on the age chart could never be seen at all.
  function selectBand(next, { navigate = false } = {}) {
    setBand(next);
    if (!next) return;
    setException(null);
    if (navigate) setLevel('L2');
  }

  // Picking a part category sends you to the part-level list filtered to it —
  // the question "what is in Engine" is only answerable at line level, and
  // leaving the reader on a totals row makes them find that list themselves.
  function pickPartCategory(name) {
    setPartCategory(name);
    setLevel('L2');
  }

  function changeLevel(next) {
    setLevel(next);
    setException(null);
    setBand(null);
    // L0 is the whole company by definition; carrying a cost center into it
    // would show one yard under a heading that says the whole company.
    if (next === 'L0') setCcFilter('all');
  }

  if (summaryLoading) return <LoadingSpinner />;

  const kpis    = summary?.kpis || {};
  const prev    = summary?.previous || {};
  const hasData = Number(kpis.item_count || 0) > 0;
  const agedPct = kpis.total_value ? Math.round((kpis.aged_12_months_value / kpis.total_value) * 100) : 0;

  // One word on every level. This used to say "Cost Center" on L1 and
  // "Division" on L0 — the same entity under two names, on a board people move
  // up and down. Readers reported it as a bug, which is the answer to whether
  // the distinction was worth keeping.
  //
  // The one word is "Cost Center", matching the rest of the portal rather than
  // this board alone. It was briefly "Division"; that made Inventory the only
  // page using it, so the same 403 from the same access check would have named
  // the entity differently depending on which report you came from.
  const ccLabel = 'Cost Center';

  // Exception and Part Class are consumed only by a part LIST — the scoped
  // cost center detail on L1, and the L2 worklist. On L0, and on L1 before a
  // cost center is picked, nothing on screen reads them, so setting one looks
  // like a filter that did nothing. Naming them beats leaving the reader to
  // conclude the data is wrong.
  const listOnScreen = level === 'L2' || (level === 'L1' && scoped);
  const inertFilters = listOnScreen
    ? []
    : [exception && 'Exception', classification && 'Part Class', partQuery && 'Part Number'].filter(Boolean);

  // What L2's worklist actually filters on. Below-minimum is the default only
  // when nothing else was picked: with an age band selected, forcing it as well
  // would silently list the intersection of the two.
  const listException = exception || (band ? null : 'under_min');

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Reports"
        title={title}
        subtitle={subtitle || (
          'Parts and spares stock, reconciled from Fleetio/Dossier. '
          + (inventoryViewIsUniversal
            ? 'Every signed-in user sees every cost center here; the figures are company-wide unless you narrow them above.'
            : 'Access here follows the same cost centers assigned for Overtime and Fuel Report.')
        )}
        actions={headerLink}
        meta={kpis.snapshot_as_of_date ? (
          <span style={s.snapshotChip}>Snapshot as of {fmtDate(kpis.snapshot_as_of_date)}</span>
        ) : undefined}
      />

      {error || !hasData ? (
        <div style={s.emptyCard}>
          <Boxes size={28} color={c.textFaint} />
          <div style={s.message}>
            {error ? (error.message || 'You do not have access to Inventory.') : 'No inventory data has been imported yet.'}
          </div>
        </div>
      ) : (
        <div style={s.board}>
          <LevelBar level={level} onChange={changeLevel} />

          <div style={s.boardBody}>
            {/* ── Filters ─────────────────────────────────────────── */}
            <div style={s.filterRow}>
              <div style={s.filterGroup}>
                <label style={s.label} htmlFor="inv-cc">{ccLabel}</label>
                <select
                  id="inv-cc"
                  style={s.select}
                  value={ccFilter}
                  onChange={(e) => setCcFilter(e.target.value)}
                >
                  <option value="all">All {ccLabel}s</option>
                  {ccOptions.map((row) => (
                    <option key={row.cc_id} value={row.cc_id}>{row.code} — {row.name}</option>
                  ))}
                </select>
              </div>

              {/* The only free-text control on the board. Contains-match, so a
                  reader with the tail of a number off a bin label finds it —
                  see buildWorklistQuery for why prefix matching was not enough. */}
              <div style={s.filterGroup}>
                <label style={s.label} htmlFor="inv-part">Part Number</label>
                <div style={s.searchWrap}>
                  <Search size={13} color={c.textFaint} />
                  <input
                    id="inv-part"
                    style={s.searchInput}
                    type="search"
                    placeholder="Contains…"
                    value={partSearch}
                    onChange={(e) => setPartSearch(e.target.value)}
                    maxLength={100}
                  />
                  {partSearch && (
                    <button
                      style={s.searchClear}
                      onClick={() => setPartSearch('')}
                      aria-label="Clear part number search"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>

              <div style={s.filterGroup}>
                <label style={s.label} htmlFor="inv-class">Part Class</label>
                <select
                  id="inv-class"
                  style={s.selectSm}
                  value={classification || ''}
                  onChange={(e) => setClassification(e.target.value || null)}
                >
                  <option value="">All classes</option>
                  {(summary?.by_classification || []).map((cl) => (
                    <option key={cl.classification} value={cl.classification}>
                      {cl.classification} ({fmtNumber(cl.item_count)})
                    </option>
                  ))}
                </select>
              </div>

              <div style={s.filterGroup}>
                <label style={s.label} htmlFor="inv-band">Age Band</label>
                <select
                  id="inv-band"
                  style={s.selectSm}
                  value={band || ''}
                  onChange={(e) => selectBand(e.target.value || null)}
                >
                  <option value="">Any age</option>
                  {AGE_BAND_ORDER.map((key) => (
                    <option key={key} value={key}>{BAND_LABELS[key]}</option>
                  ))}
                </select>
              </div>

              <div style={s.filterGroup}>
                <label style={s.label} htmlFor="inv-exc">Exception</label>
                <select
                  id="inv-exc"
                  style={s.selectSm}
                  value={exception || ''}
                  onChange={(e) => { setException(e.target.value || null); setBand(null); }}
                >
                  <option value="">None</option>
                  {Object.entries(EXCEPTION_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Part category arrives by clicking a breakout row rather than
                  from a control up here, so it needs its own visible chip —
                  a filter with no on-screen presence is one people forget is
                  applied and then read the numbers as company-wide. */}
              {partCategory && (
                <button style={s.chip} onClick={() => setPartCategory(null)}>
                  {partCategory} <X size={12} />
                </button>
              )}

              {(ccFilter !== 'all' || exception || band || classification || partCategory || partSearch) && (
                <button
                  style={s.clearBtn}
                  onClick={() => {
                    setCcFilter('all'); setException(null); setBand(null);
                    setClassification(null); setPartCategory(null); setPartSearch('');
                  }}
                >
                  <X size={12} /> Clear all
                </button>
              )}
            </div>

            {!!inertFilters.length && (
              <div style={s.noteBox}>
                <HelpCircle size={14} color={c.textMuted} />
                <span>
                  {inertFilters.join(' and ')} {inertFilters.length > 1 ? 'filter' : 'filters'} the
                  part list, which this level does not show — the totals above are unfiltered by
                  {' '}{inertFilters.length > 1 ? 'them' : 'it'}.{' '}
                  <button style={s.linkInline} onClick={() => setLevel('L2')}>
                    Show the part list
                  </button>
                </span>
              </div>
            )}

            {kpis.unmapped_value > 0 && (
              <div style={s.warningBox}>
                <AlertTriangle size={14} color={c.warningText} />
                <span>
                  {fmtMoney(kpis.unmapped_value)} of inventory value sits at locations not yet mapped to a cost
                  center, and is excluded from the totals below until mapped.
                </span>
              </div>
            )}

            {/* ── KPI row ─────────────────────────────────────────── */}
            <div style={s.kpiRow}>
              {level === 'L2' ? (
                <>
                  <StatTile
                    icon={<PackageX size={16} />}
                    label="Reorder Due"
                    value={fmtNumber(kpis.under_min_count)}
                    sub={`${fmtMoney(kpis.under_min_value)} below minimum`}
                    tone="warning"
                    onClick={() => drillTo('under_min')}
                    active={exception === 'under_min'}
                    title="Below minimum. Fleetio holds no separate reorder point, so Min is what this counts against."
                  />
                  <StatTile
                    icon={<TrendingDown size={16} />}
                    label="Over-Max"
                    value={fmtNumber(kpis.over_max_real_count)}
                    sub={`${fmtMoney(kpis.over_max_real_value)} excess`}
                    tone="warning"
                    onClick={() => drillTo('over_max')}
                    active={exception === 'over_max'}
                  />
                  <StatTile
                    icon={<ShieldAlert size={16} />}
                    label="Below Min — Critical"
                    value={fmtNumber(kpis.under_min_critical_count)}
                    sub={`${fmtMoney(kpis.under_min_critical_value)} short · fleet risk`}
                    tone="danger"
                    onClick={() => drillTo('under_min_critical')}
                    active={exception === 'under_min_critical'}
                  />
                  <StatTile
                    icon={<Archive size={16} />}
                    label="Disposition Candidates"
                    value={fmtNumber(kpis.idle_24_count)}
                    sub={`${fmtMoney(kpis.idle_24_value)} idle 24+ months`}
                    tone="warning"
                    onClick={() => drillTo('idle_24_months')}
                    active={exception === 'idle_24_months'}
                    title="Idle 24+ months, including never used. Two of the three write-down criteria in the spec — open work-order demand and superseded part numbers — need feeds we do not have."
                  />
                </>
              ) : (
                <>
                  <StatTile
                    icon={<DollarSign size={16} />}
                    label="Total Value"
                    value={fmtMoney(kpis.total_value)}
                    sub={`${fmtNumber(kpis.item_count)} lines`}
                    current={kpis.total_value} previous={prev.total_value}
                  />
                  {/* No onClick on any of these, deliberately. L0 and L1 report;
                      L2 is where a reader filters and exports for themselves.
                      StatTile renders a plain div without a handler, so these
                      stop looking clickable rather than looking clickable and
                      doing nothing.

                      They used to drill into L2 carrying an exception filter —
                      below-minimum, over-max, idle 24+, critical spares below
                      min. L2's own filters are Cost Center, Part Class, Age Band
                      and Part Category, so a tile landed the reader on a screen
                      that could not say what had been clicked. The tiles on L2
                      DO still toggle, because there they filter the list in
                      front of you instead of teleporting you to one. */}
                  <StatTile
                    icon={<Archive size={16} />}
                    label="Aged +12 mo"
                    value={fmtMoney(kpis.aged_12_months_value)}
                    sub={`${agedPct}% of on hand · ${fmtNumber(kpis.aged_12_months_count)} lines · excl. critical spares`}
                    tone="warning"
                    current={kpis.aged_12_months_value} previous={prev.aged_12_months_value} goodDirection="down"
                  />
                  <StatTile
                    icon={<HelpCircle size={16} />}
                    label="Critical Spare +12 mo"
                    value={fmtMoney(kpis.critical_spare_value)}
                    sub={`${fmtNumber(kpis.critical_spare_count)} lines · counted here, not under Aged`}
                    tone="warning"
                    title="At Procurement's request the export routes aged critical spares to their own category rather than into Aged 6–12 or Aged +12."
                    current={kpis.critical_spare_value} previous={prev.critical_spare_value} goodDirection="down"
                  />
                  <StatTile
                    icon={<TrendingDown size={16} />}
                    label="Over Maximum"
                    value={fmtNumber(kpis.over_max_real_count)}
                    sub={`${fmtMoney(kpis.over_max_real_value)} excess · ${fmtNumber(kpis.over_max_nomax_count)} more have no maximum set`}
                    tone="warning"
                    title="Counted only against a maximum somebody actually set. Lines with a maximum of 0 are a configuration gap, not excess stock."
                    current={kpis.over_max_real_count} previous={prev.over_max_real_count} goodDirection="down"
                  />
                  <StatTile
                    icon={<ShieldAlert size={16} />}
                    label="Critical Spares Below Min"
                    value={fmtNumber(kpis.under_min_critical_count)}
                    sub={`${fmtMoney(kpis.under_min_critical_value)} short · fleet availability risk`}
                    tone="danger"
                    current={kpis.under_min_critical_count}
                    previous={prev.under_min_critical_count} goodDirection="down"
                  />
                </>
              )}
            </div>

            {level !== 'L2' && kpis.over_max_nomax_count > 0 && (
              <div style={s.noteBox}>
                <AlertTriangle size={14} color={c.textMuted} />
                <span>
                  A further {fmtNumber(kpis.over_max_nomax_count)} lines ({fmtMoney(kpis.over_max_nomax_value)}) are
                  flagged over-max only because no maximum is set on them, so their whole value counts as excess.
                  That is a gap in Fleetio, not stock to move.{' '}
                  <button style={s.linkInline} onClick={() => drillTo('over_max_no_max')}>List them</button>
                </span>
              </div>
            )}

            {/* ── L0 ──────────────────────────────────────────────── */}
            {level === 'L0' && (
              <>
                <div style={s.panelRow}>
                  <div style={s.panel}><InventoryTrend /></div>
                </div>

                {/* Two dimensions, two cards, both always on screen. They were
                    one card behind a toggle, which made them read as alternative
                    views of the same number — they are not, and seeing one never
                    removes the need for the other. Neither card carries a title
                    of its own: each chart renders its own header, and a card
                    heading above that just said the same thing twice. */}
                <div style={s.card}>
                  <AgeBandByCostCenter
                    rows={summary?.by_cost_center_category}
                    nonStockRows={summary?.by_cost_center_non_stock_45}
                    costCenters={ccOptions}
                    onPick={(id) => { setLevel('L1'); setCcFilter(id); }}
                  />
                  <DataDisclosure label="Cost Center" count={tableRows.length}>
                    <CostCenterTable
                      rows={tableRows} sortBy={sortBy} onSort={setSortBy}
                      onPick={(id) => { setLevel('L1'); setCcFilter(id); }} label="Cost Center"
                    />
                  </DataDisclosure>
                </div>

                <div style={s.card}>
                  <PartCategoryBreakout
                    rows={summary?.by_part_category}
                    onPick={pickPartCategory}
                    selected={partCategory}
                  />
                </div>

                <div style={s.card}>
                  <CategoryCompositionBar kpis={kpis} byCategory={summary?.by_category} />
                  <DataDisclosure label="reporting category" count={9}>
                    <ValueCategoryTable />
                  </DataDisclosure>
                </div>

                <div style={s.panelRow}>
                  <div style={s.panel}>
                    <AgeBandChart
                      bands={summary?.by_age_band}
                      onSelect={(next) => selectBand(next, { navigate: true })}
                      selected={band}
                    />
                  </div>
                  <PendingPanel
                    title="Working capital vs reduction target"
                    needs="a reduction target, and more than one month of history"
                    detail="Every snapshot is now retained, so the trend fills in from here. The target itself has nowhere to live yet — it needs a store and someone to own setting it, by cost center or for the enterprise."
                  />
                </div>

              </>
            )}

            {/* ── L1 ──────────────────────────────────────────────── */}
            {level === 'L1' && (
              <>
                {ccFilter === 'all' ? (
                  <div style={s.card}>
                    <AgeBandByCostCenter
                      rows={summary?.by_cost_center_category}
                      nonStockRows={summary?.by_cost_center_non_stock_45}
                      costCenters={ccOptions}
                      onPick={setCcFilter}
                    />
                    <DataDisclosure label="Cost Center" count={tableRows.length}>
                      <CostCenterTable
                        rows={tableRows} sortBy={sortBy} onSort={setSortBy}
                        onPick={setCcFilter} label="Cost Center"
                      />
                    </DataDisclosure>
                  </div>
                ) : ccDetailLoading ? <LoadingSpinner /> : (
                  <div style={s.card}>
                    <div style={s.cardHeadRow}>
                      <div>
                        {ccDetail?.cost_center?.name || 'Cost Center'} — Parts
                        {(exception || band) && (
                          <span style={s.cardTitleNote}>
                            {' '}· {exception ? EXCEPTION_LABELS[exception] : BAND_LABELS[band]}
                            {' '}({fmtNumber((ccDetail?.items || []).length)})
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Open by default, unlike the reference tables: scoped to
                        one yard with an exception applied, this list IS the
                        answer the reader navigated here for, not a backing
                        table for a chart above it. */}
                    <DataDisclosure label="item" count={(ccDetail?.items || []).length} defaultOpen>
                      <ItemTable items={ccDetail?.items || []} />
                    </DataDisclosure>
                  </div>
                )}

                {/* Part categories stand alongside the cost center view at
                    every scope, including a single yard — "what is the money
                    in here" is the question a yard visit opens with. */}
                <div style={s.card}>
                  <PartCategoryBreakout
                    rows={summary?.by_part_category}
                    onPick={pickPartCategory}
                    selected={partCategory}
                  />
                </div>


                <div style={s.panelRow}>
                  <div style={s.panel}>
                    <AgeBandChart
                      bands={summary?.by_age_band}
                      onSelect={(next) => selectBand(next, { navigate: true })}
                      selected={band}
                    />
                  </div>
                  <PendingPanel
                    title="Movement — receipts, issues, adjustments"
                    needs="consecutive snapshots to compare, or Fleetio issue history"
                    detail="The export is a stock position: it records when a part last moved, never how much was consumed. Daily snapshots are now kept, so net movement can be derived from here — gross receipts vs issues still needs the feed."
                  />
                </div>

                <div style={s.panelRow}>
                  <div style={s.panel}>
                    <InventoryTrend
                      ccId={ccFilter === 'all' ? undefined : ccFilter}
                      ccName={selectedCc ? `${selectedCc.code} — ${selectedCc.name}` : undefined}
                    />
                  </div>
                  <PendingPanel
                    title="Turns vs value"
                    needs="Fleetio issue history"
                    detail="Turns separate the two populations this board merges today: a critical spare that never moves is correct, a consumable that never moves is trapped capital. Nothing in the current export can tell them apart."
                  />
                </div>


                <div style={s.card}>
                  <div style={s.cardTitle}>
                    Held at More Than One Yard
                    <span style={s.cardTitleNote}> · check before buying</span>
                  </div>
                  <DataDisclosure label="part">
                    <CrossYardPanel ccId={scoped ? ccFilter : undefined} />
                  </DataDisclosure>
                </div>
              </>
            )}

            {/* ── L2 ──────────────────────────────────────────────── */}
            {level === 'L2' && (
              <>
                {/* Parts totals by category, above the worklist rather than
                    behind a toggle. At storeroom level this is the question
                    that decides what to go and count, so it leads; clicking a
                    row filters everything below it to that category. */}
                <div style={s.card}>
                  <div style={s.cardHeadRow}>
                    <div>
                      Parts Totals by Category
                      <span style={s.cardTitleNote}>
                        {' '}· {scoped ? 'this cost center' : 'all cost centers'} · click to filter the worklist
                      </span>
                    </div>
                  </div>
                  <PartCategoryBreakout
                    rows={summary?.by_part_category}
                    selected={partCategory}
                    onPick={(name) => setPartCategory(name === partCategory ? null : name)}
                  />
                </div>

                <div style={s.card}>
                  <div style={s.cardHeadRow}>
                    <div>
                      Part-Level Worklist
                      {/* Name the population actually listed. The default is
                          below-minimum, not "everything", and a heading that
                          overstates its list is how a reader concludes an
                          exception was clean when it was never queried. */}
                      {/* Name the population actually listed, including the
                          case where an age band brought the reader here. The
                          below-minimum default applies ONLY when no filter was
                          chosen — defaulting on top of a band would list the
                          intersection while the heading named just the band. */}
                      <span style={s.cardTitleNote}>
                        {' '}· {band ? BAND_LABELS[band] : EXCEPTION_LABELS[listException]}, largest exposure first
                        {partCategory ? ` · ${partCategory}` : ''}
                        {partQuery ? ` · part number contains “${partQuery}”` : ''}
                        {scoped && selectedCc ? ` · ${selectedCc.name}` : ''}
                      </span>
                    </div>
                    <ExportButton
                      params={{
                        ...(listException ? { exception: listException } : {}),
                        ...(band ? { band } : {}),
                        ...(ccFilter === 'all' ? {} : { cc_id: ccFilter }),
                        ...(classification ? { classification } : {}),
                        ...(partCategory ? { part_category: partCategory } : {}),
                        ...(partQuery ? { part_number: partQuery } : {}),
                      }}
                    />
                  </div>
                  <WorklistTable
                    exception={listException}
                    band={band}
                    ccId={ccFilter === 'all' ? undefined : ccFilter}
                    classification={classification}
                    partCategory={partCategory}
                    partNumber={partQuery}
                  />
                </div>

                {/* Full width, and no longer paired with the write-downs
                    placeholder. This is a worklist -- part number, description,
                    class, cost center, bin, min/max -- and it was rendering in
                    half a row beside a panel describing a feature that does not
                    exist. The columns were the ones being squeezed, so the
                    reader lost real data to make room for a note about absent
                    data. The write-downs gap is still real and still worth
                    building; PendingPanel remains on L1, where it sits beside
                    panels of comparable weight rather than in front of a table. */}
                <div style={{ ...s.panel, marginBottom: sp[5] }}>
                  <div style={s.panelHead}>
                    <div style={s.panelTitle}>Below Minimum — Critical Spares</div>
                    <div style={s.panelSub}>A fleet-availability risk, never netted against over-max value</div>
                  </div>
                  <WorklistTable
                    exception="under_min_critical"
                    ccId={ccFilter === 'all' ? undefined : ccFilter}
                    classification={classification}
                    partCategory={partCategory} band={band} limit={25}
                  />
                </div>

                <div style={s.card}>
                  <div style={s.cardTitle}>
                    Held at More Than One Yard
                    <span style={s.cardTitleNote}> · pull before you buy</span>
                  </div>
                  <DataDisclosure label="part">
                    <CrossYardPanel ccId={scoped ? ccFilter : undefined} />
                  </DataDisclosure>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-tables ───────────────────────────────────────────────────────────────

// Chart above, table behind a disclosure — the pairing the accessibility pass
// requires anyway (a table view alongside the marks), and the reason the chart
// can stay ranked-and-folded without hiding anything: the exact figure for a
// category outside the top twelve is always one click away.
function PartCategoryBreakout({ rows, onPick, selected }) {
  return (
    <>
      <PartCategoryChart rows={rows} onPick={onPick} selected={selected} />
      <DataDisclosure label="Part Category" count={rows?.length}>
        <PartCategoryTable rows={rows} onPick={onPick} />
      </DataDisclosure>
    </>
  );
}

// Downloads the worklist as a workbook, passing the SAME filter the table is
// showing. The server builds the file from one shared query, so the export
// cannot disagree with the screen it was taken from.
//
// It goes through api.getBlob rather than a plain link because the API is
// bearer-token authenticated — an <a href> carries no Authorization header and
// would land on the login page instead of a file.
function ExportButton({ params }) {
  const { api } = useInventorySource();
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  async function run() {
    setBusy(true);
    setError('');
    try {
      const qs   = new URLSearchParams(params).toString();
      const blob = await api.getBlob(`/inventory/worklist/export?${qs}`);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      // The server names the file for its filter; this is only the fallback
      // for browsers that ignore Content-Disposition on a blob URL.
      a.download = `inventory-worklist-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Export failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.exportWrap}>
      {error && <span style={s.exportErr}>{error}</span>}
      <button style={s.exportBtn} onClick={run} disabled={busy}>
        <Download size={13} />
        {busy ? 'Preparing…' : 'Export to Excel'}
      </button>
    </div>
  );
}

// Idle % is derived rather than stored, so it is computed once here and sorted
// on like any other column — otherwise sorting by it would order on a value the
// table never actually holds.
const CC_COLUMNS = [
  { key: 'name',                label: null,             align: 'left'  },
  { key: 'item_count',          label: 'Part Lines',     align: 'right' },
  { key: 'distinct_part_count', label: 'Distinct Parts', align: 'right' },
  { key: 'total_value',         label: 'Value',          align: 'right' },
  { key: 'aged_value',       label: 'Idle 12+',    align: 'right' },
  { key: 'aged_pct',         label: 'Idle %',      align: 'right' },
  { key: 'over_max_value',   label: 'Over-Max $',  align: 'right' },
  { key: 'under_min_value',  label: 'Below Min $', align: 'right' },
];

function CostCenterTable({ rows, onPick, label, sortBy, onSort }) {
  const withPct = rows.map((row) => ({
    ...row,
    aged_pct: row.total_value ? (row.aged_value / row.total_value) * 100 : 0,
  }));

  const sorted = [...withPct].sort((a, b) => {
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    if (sortBy.key === 'name') return dir * String(a.code).localeCompare(String(b.code));
    return dir * (Number(a[sortBy.key] || 0) - Number(b[sortBy.key] || 0));
  });

  // Clicking the active column flips direction; a new column starts descending,
  // because every column here is a magnitude and the large end is the question.
  function head(col) {
    const active = sortBy.key === col.key;
    return (
      <th key={col.key} style={col.align === 'right' ? s.thR : s.th}>
        <button
          style={{ ...s.sortBtn, color: active ? c.navy : c.textMuted }}
          onClick={() => onSort({ key: col.key, dir: active && sortBy.dir === 'desc' ? 'asc' : 'desc' })}
        >
          {col.label ?? label}
          <span style={s.sortCaret}>{active ? (sortBy.dir === 'desc' ? '▾' : '▴') : ''}</span>
        </button>
      </th>
    );
  }

  return (
    <div style={s.tableScroll}>
      <table style={s.table}>
        <thead>
          <tr>{CC_COLUMNS.map(head)}</tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const pct = row.total_value ? Math.round((row.aged_value / row.total_value) * 100) : 0;
            return (
              <tr key={row.cc_id} style={s.tr}>
                <td style={s.td}>
                  <button style={s.linkBtn} onClick={() => onPick(row.cc_id)}>
                    {row.code} — {row.name}
                  </button>
                </td>
                <td style={s.tdR}>{fmtNumber(row.item_count)}</td>
                <td style={s.tdR}>{fmtNumber(row.distinct_part_count)}</td>
                <td style={s.tdR}>{fmtMoney(row.total_value)}</td>
                <td style={s.tdR}>{fmtMoney(row.aged_value)}</td>
                <td style={{ ...s.tdR, color: pct >= 50 ? c.warningText : c.textStrong }}>{pct}%</td>
                <td style={s.tdR}>{row.over_max_value ? fmtMoney(row.over_max_value) : '—'}</td>
                <td style={{ ...s.tdR, color: row.under_min_critical_count > 0 ? c.dangerText : c.textStrong }}>
                  {row.under_min_value ? fmtMoney(row.under_min_value) : '—'}
                </td>
              </tr>
            );
          })}
          {!sorted.length && (
            <tr><td colSpan={8} style={s.empty}>No cost centers with mapped inventory yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ItemTable({ items }) {
  return (
    <div style={s.tableScroll}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Part #</th>
            <th style={s.th}>Description</th>
            <th style={s.th}>Class</th>
            <th style={s.th}>Bin</th>
            <th style={s.thR}>Qty</th>
            <th style={s.thR}>Min / Max</th>
            <th style={s.thR}>Value</th>
            <th style={s.th}>Last Issued</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} style={s.tr}>
              <td style={s.td}>{it.part_number}</td>
              <td style={s.tdDesc} title={it.description}>{it.description}</td>
              <td style={s.td}>{it.classification}</td>
              <td style={s.td}>{fmtBin(it)}</td>
              <td style={s.tdR}>{fmtNumber(it.current_quantity)}</td>
              <td style={{ ...s.tdR, color: it.max_not_set ? c.textFaint : c.textStrong }}>
                {fmtNumber(it.min_stock_level)} / {it.max_not_set ? 'not set' : fmtNumber(it.max_stock_level)}
              </td>
              <td style={s.tdR}>{fmtMoney(it.inventory_value)}</td>
              <td style={{ ...s.td, color: it.last_used_date ? c.textStrong : c.warningText }}>
                {it.last_used_date ? fmtDate(it.last_used_date) : 'never'}
              </td>
            </tr>
          ))}
          {!items.length && (
            <tr><td colSpan={8} style={s.empty}>No parts match this filter at this cost center.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const s = {
  page: { maxWidth: '1180px', margin: '0 auto' },
  board: {
    ...card.base,
    padding: 0, overflow: 'hidden',
  },
  boardBody: { padding: sp[5], background: c.canvas },

  emptyCard: {
    ...card.base,
    padding: `${sp[10]} ${sp[6]}`, display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: sp[3], textAlign: 'center',
  },
  message:     { ...t.bodySm, color: c.textMuted },
  snapshotChip: {
    ...t.micro, color: c.textMuted, background: c.canvas,
    border: `1px solid ${c.border}`, borderRadius: r.pill, padding: '3px 10px',
  },

  filterRow:   { display: 'flex', alignItems: 'flex-end', gap: sp[3], marginBottom: sp[4], flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: sp[1] },
  label:       { ...t.micro, fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' },
  sortBtn: {
    background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer',
    fontWeight: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: '3px',
  },
  sortCaret: { fontSize: '9px', width: '7px', display: 'inline-block' },
  select: {
    padding: '7px 10px', border: `1px solid ${c.borderStrong}`, borderRadius: r.md,
    ...t.bodySm, color: c.textStrong, fontFamily: 'inherit', minWidth: '220px', background: c.surface,
  },
  selectSm: {
    padding: '7px 10px', border: `1px solid ${c.borderStrong}`, borderRadius: r.md,
    ...t.bodySm, color: c.textStrong, fontFamily: 'inherit', minWidth: '150px', background: c.surface,
  },
  // The border lives on the wrapper, not the input, so the icon and the clear
  // button sit inside one control rather than beside a boxed field.
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: sp[1],
    padding: '7px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: r.md,
    background: c.surface, minWidth: '170px',
  },
  searchInput: {
    flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
    ...t.bodySm, color: c.textStrong, fontFamily: 'inherit',
  },
  searchClear: {
    display: 'flex', alignItems: 'center', background: 'none', border: 'none',
    padding: 0, cursor: 'pointer', color: c.textFaint,
  },
  clearBtn:   { ...btn.secondaryXs, background: c.surface, gap: sp[1] },
  linkBtn:    { ...btn.link, textAlign: 'left' },
  linkInline: { ...btn.linkXs },
  chip: {
    ...btn.secondaryXs, gap: sp[1],
    background: c.navy, color: c.onNavy, borderColor: c.navy,
  },

  warningBox: {
    display: 'flex', alignItems: 'flex-start', gap: sp[2], marginBottom: sp[4],
    padding: `${sp[2]} ${sp[3]}`, background: c.warningTint,
    border: `1px solid ${c.warning}`, borderRadius: r.lg,
    ...t.caption, color: c.warningText, lineHeight: 1.5,
  },
  noteBox: {
    display: 'flex', alignItems: 'flex-start', gap: sp[2], marginBottom: sp[5],
    padding: `${sp[2]} ${sp[3]}`, background: c.surface,
    border: `1px solid ${c.border}`, borderRadius: r.lg,
    ...t.caption, color: c.textMuted, lineHeight: 1.5,
  },

  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: sp[3], marginBottom: sp[5],
  },
  // Two-up where there is room, stacking below ~740px. `alignItems: start` so a
  // short panel does not stretch to match a tall neighbour.
  panelRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: sp[4], marginBottom: sp[5], alignItems: 'start',
  },
  panel:      { ...card.base, overflow: 'hidden' },
  panelHead:  { padding: `${sp[4]} ${sp[5]} 0` },
  panelTitle: { ...t.h3, color: c.navy },
  panelSub:   { ...t.micro, color: c.textFaint, marginTop: '2px', marginBottom: sp[2] },

  card:          { ...card.base, overflow: 'hidden', marginBottom: sp[5] },
  cardTitle:     { ...card.head },
  cardTitleNote: { ...t.micro, color: c.textFaint, fontWeight: 400 },
  // A card head that carries a control as well as a title. Wraps rather than
  // compressing, so the segmented control keeps its labels on a narrow screen.
  cardHeadRow: {
    ...card.head, display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: sp[3], flexWrap: 'wrap',
  },
  exportWrap: { display: 'flex', alignItems: 'center', gap: sp[2] },
  exportBtn:  { ...btn.secondarySm, gap: sp[1] },
  exportErr:  { ...t.micro, color: c.dangerText },
  tableScroll:   { overflowX: 'auto' },
  table:         { width: '100%', borderCollapse: 'collapse' },
  th:            { ...tbl.th, whiteSpace: 'nowrap' },
  thR:           { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:            { ...tbl.tr },
  td:            { ...tbl.td, whiteSpace: 'nowrap' },
  tdR:           { ...tbl.tdR },
  tdDesc: {
    ...tbl.td, maxWidth: '260px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  empty: { textAlign: 'center', padding: sp[6], color: c.textFaint, ...t.bodySm },
};
